/**
 * 👥⚔️ Wilds Party Combat Overlay (Feb 2026)
 *
 * Polling-based real-time party-vs-mob combat. Owns its own polling loop
 * (~2.5s cadence). Shows turn order, party HP bars, mob HP, scrolling battle
 * log. When it's the viewer's turn, shows Heavy/Light action buttons.
 * Auto-closes on encounter resolution → callback fires reward screen.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SpriteFighter, SpriteStyles } from "@/components/battle/SpriteFighter";
import SkillPicker from "@/components/combat/SkillPicker";
import BattleBagButton from "@/components/combat/BattleBagButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;

export default function WildsPartyCombat({ encounterId, viewerUserId, isLeader, onClose }) {
  const [encounter, setEncounter] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [logTick, setLogTick] = useState(0);          // for ticking countdowns
  const seenTurnsRef = useRef(new Set());
  // 🎯 Feb 2026: skills the viewer can cast (resolves from /api/encounter/skills)
  const [wiredSkills, setWiredSkills] = useState([]);

  // 🎞️ Transient combat effect derived from the newest log entry.
  // Same pattern as BattlePage — chain effects so the hero attack animation
  // doesn't get stomped by the mob counter-strike on the next poll.
  const [effect, setEffect] = useState(null);
  const lastLogLenRef = useRef(0);
  const effectChainRef = useRef([]);
  const effectTimerRef = useRef(null);
  const EFFECT_MS = 900;

  const playNextEffect = () => {
    const q = effectChainRef.current;
    if (q.length === 0) { setEffect(null); effectTimerRef.current = null; return; }
    const next = q.shift();
    setEffect(next);
    effectTimerRef.current = setTimeout(playNextEffect, EFFECT_MS);
  };

  // Derive an effect from a wilds-encounter log entry
  const deriveEffectFromWildsLog = (e) => {
    if (!e || !e.type) return null;
    if (e.type === "hero_attack") {
      const vfx = [];
      if (e.crit) vfx.push("crit");
      return {
        kind: e.move === "heavy" ? "atk_p_heavy" : "atk_p",
        attackerUserId: e.actor,
        targetSide: "mob",
        vfx,
        dmg: e.dmg || 0,
        crit: !!e.crit,
      };
    }
    if (e.type === "mob_attack") {
      return {
        kind: "atk_p",
        attackerSide: "mob",
        // Fallback: when backend didn't include target_user_id (older
        // encounters), assume the mob hit the viewer so SOMETHING animates.
        targetUserId: e.target_user_id || viewerUserId || null,
        vfx: [],
        dmg: e.dmg || 0,
      };
    }
    return null;  // skip / round_start / system → no visual
  };

  // Polling loop
  useEffect(() => {
    if (!encounterId) return;
    let alive = true;
    let timer = null;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/encounter/${encounterId}`);
        if (!alive) return;
        const enc = r.data?.encounter;
        setEncounter(enc);
        setError(null);
        // Battle-log toast for new entries
        if (enc?.log) {
          for (const e of enc.log) {
            const key = `${e.turn || 0}:${e.type}:${e.actor || ""}:${e.msg || ""}`;
            if (!seenTurnsRef.current.has(key)) {
              seenTurnsRef.current.add(key);
            }
          }
        }
        // 🎞️ Effect derivation — chain newly-arrived log entries
        const logLen = enc?.log?.length || 0;
        if (logLen < lastLogLenRef.current) {
          // Log shrunk → reset (defensive)
          lastLogLenRef.current = 0;
          effectChainRef.current = [];
          if (effectTimerRef.current) {
            clearTimeout(effectTimerRef.current);
            effectTimerRef.current = null;
          }
          setEffect(null);
        }
        if (logLen > lastLogLenRef.current) {
          const newSlice = (enc.log || []).slice(lastLogLenRef.current);
          newSlice.forEach((entry, idx) => {
            const d = deriveEffectFromWildsLog(entry);
            if (d) {
              effectChainRef.current.push({
                ...d,
                key: `${lastLogLenRef.current + idx}-${entry.type}`,
              });
            }
          });
          if (!effectTimerRef.current && effectChainRef.current.length > 0) {
            playNextEffect();
          }
        }
        lastLogLenRef.current = logLen;
        // Stop polling once the encounter is over
        if (enc?.status !== "active") {
          return;
        }
      } catch (e) {
        if (!alive) return;
        const detail = e?.response?.data?.detail || "Couldn't poll the encounter.";
        setError(detail);
      }
      if (alive) {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [encounterId]);

  // Re-render every second so the AFK timer countdown updates
  useEffect(() => {
    const id = setInterval(() => setLogTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // 🧹 Cleanup the effect-chain timer on unmount
  useEffect(() => () => {
    if (effectTimerRef.current) clearTimeout(effectTimerRef.current);
  }, []);

  // 🎭 Map an effect → role for a given party slot (hero/mob)
  const roleForMob = (eff) => {
    if (!eff) return null;
    if (eff.attackerSide === "mob") return "attacker";
    if (eff.targetSide === "mob") return "target";
    return null;
  };
  const roleForHero = (eff, userId) => {
    if (!eff) return null;
    if (eff.attackerUserId === userId) return "attacker";
    if (eff.targetUserId === userId) return "target";
    return null;
  };

  const onMove = async (move) => {
    if (!encounter || submitting) return;
    setSubmitting(true);
    // Accept either a legacy string ("heavy"/"light"/"skill:<id>") or a rich
    // skill object `{move: "skill", skill_id}` from SkillPicker.
    let wireMove = move;
    if (typeof move === "object" && move !== null) {
      wireMove = move.move === "skill" && move.skill_id
        ? `skill:${move.skill_id}`
        : move.move;
    }
    try {
      const r = await axios.post(`${API}/encounter/${encounterId}/move`, { move: wireMove });
      setEncounter(r.data?.encounter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch wired class skills once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/encounter/skills`);
        if (!cancelled) setWiredSkills(r.data?.skills || []);
      } catch (e) {
        // Silent — server autoroutes any legacy heavy/light input to the class Generator.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onForfeit = async () => {
    if (!encounter || submitting) return;
    if (!window.confirm("Forfeit this encounter? No rewards, no penalty.")) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/encounter/${encounterId}/forfeit`);
      toast.success("🏳️ Encounter forfeited.");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forfeit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!encounterId) return null;

  if (!encounter) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
           data-testid="party-combat-loading">
        <div className="text-amber-200 text-lg animate-pulse">⚔️ Engaging…</div>
      </div>
    );
  }

  const isViewerTurn = encounter.current_actor_is_viewer && encounter.status === "active";
  const isMobTurn = encounter.current_actor_is_mob && encounter.status === "active";
  const ttl = encounter.turn_timeout_at ? new Date(encounter.turn_timeout_at).getTime() : null;
  // 🎯 Mar 5, 2026 owner canon: Wilds combat never times out except at the
  // top-of-the-hour roster refresh. Per-turn auto-attack is capped at 60s
  // (server's TURN_TIMEOUT_SECONDS). If a client/server clock skew or a
  // stale doc emits a huge remaining value, we clamp to 60 so the UI never
  // shows nonsense like "auto-attack in 25218s".
  const rawSecs = ttl ? Math.max(0, Math.floor((ttl - Date.now()) / 1000)) : null;
  const secsToTimeout = rawSecs != null ? Math.min(60, rawSecs) : null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/95 flex flex-col p-3 sm:p-5 overflow-y-auto"
      data-testid="party-combat-overlay"
    >
      <SpriteStyles />
      <div className="w-full max-w-3xl mx-auto">
        {/* Header bar */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-red-400/70">
              Party Encounter · Round {encounter.round}
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-red-200"
                style={{ fontFamily: "Anton, sans-serif" }}>
              ⚔️ {encounter.mob?.name?.toUpperCase() || "MONSTER"}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {isLeader && encounter.status === "active" && (
              <button
                onClick={onForfeit}
                disabled={submitting}
                data-testid="party-combat-forfeit"
                className="px-3 py-1.5 rounded-md bg-zinc-700 hover:bg-red-700 disabled:opacity-50 text-red-200 text-xs font-bold"
              >
                🏳️ Forfeit
              </button>
            )}
            {encounter.status !== "active" && (
              <button
                onClick={onClose}
                data-testid="party-combat-close"
                className="px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-black text-sm font-black tracking-wider"
              >
                ✕ Close
              </button>
            )}
          </div>
        </div>

        {/* Mob card */}
        <MobBattleCard mob={encounter.mob}
                       isCurrent={isMobTurn}
                       status={encounter.status}
                       role={roleForMob(effect)}
                       effect={effect} />

        {/* Turn indicator strip */}
        <div className="my-3 rounded-lg bg-gradient-to-r from-purple-950/80 via-black to-purple-950/80 border border-purple-700/40 p-3 text-center"
             data-testid="party-combat-turn-banner">
          {encounter.status === "active" && (
            <>
              {isViewerTurn && (
                <div className="text-amber-200 font-black text-lg" data-testid="party-combat-your-turn">
                  🗡️ YOUR TURN — strike!
                </div>
              )}
              {!isViewerTurn && !isMobTurn && (
                <div className="text-purple-200 text-sm">
                  ⏳ Waiting for{" "}
                  <span className="font-black">
                    {encounter.heroes.find((h) => h.user_id === encounter.current_actor)?.name || "ally"}
                  </span>
                  {secsToTimeout != null && <span className="text-purple-400 text-[10px] ml-2">· auto-attack in {secsToTimeout}s</span>}
                </div>
              )}
              {isMobTurn && (
                <div className="text-red-300 font-black text-base animate-pulse">
                  💢 {encounter.mob.name} winds up to strike…
                </div>
              )}
            </>
          )}
          {encounter.status === "won" && (
            <div className="text-amber-300 font-black text-2xl" style={{ fontFamily: "Anton, sans-serif" }}>
              🏆 VICTORY
            </div>
          )}
          {encounter.status === "lost" && (
            <div className="text-red-400 font-black text-2xl" style={{ fontFamily: "Anton, sans-serif" }}>
              💀 PARTY WIPED
            </div>
          )}
          {encounter.status === "abandoned" && (
            <div className="text-zinc-300 font-black text-xl">🏳️ FORFEITED</div>
          )}
          {encounter.status === "expired" && (
            <div className="text-zinc-300 font-black text-xl">⌛ ENCOUNTER EXPIRED</div>
          )}
        </div>

        {/* Heroes row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="party-combat-heroes">
          {encounter.heroes.map((h) => (
            <HeroBattleCard key={h.user_id}
                            hero={h}
                            isCurrent={encounter.current_actor === h.user_id && encounter.status === "active"}
                            role={roleForHero(effect, h.user_id)}
                            effect={effect} />
          ))}
        </div>

        {/* 💠 Battlefield Mana Bar — Mar 2026 (owner directive: mana on
            multi-battles + all arenas). Reads the viewer's own mana out of
            the encounter payload so hunts and coop combats show live
            resource state without opening the picker. */}
        <div
          className="mb-3 px-2 py-1.5 bg-black/60 border border-cyan-900/60"
          data-testid="party-combat-mana"
        >
          <div className="flex items-center justify-between text-[9px] uppercase tracking-widest mb-0.5">
            <span className="text-cyan-300/70 font-bold">💠 Your Mana</span>
            <span className="tabular-nums text-cyan-300 font-bold" data-testid="party-combat-mana-value">
              {encounter?.viewer_mana ?? encounter?.mana ?? 0} / {encounter?.viewer_mana_cap ?? encounter?.mana_cap ?? 100}
            </span>
          </div>
          <div className="h-1.5 bg-black overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-400 transition-all duration-500"
              style={{
                width: `${Math.min(100, Math.max(0, Math.round(((encounter?.viewer_mana ?? encounter?.mana ?? 0) / Math.max(1, encounter?.viewer_mana_cap ?? encounter?.mana_cap ?? 100)) * 100)))}%`,
              }}
            />
          </div>
        </div>

        {/* 🤝 Combo Ultimates — party co-op only, fires when both partner
            classes are in this encounter, both alive, both meet mana +
            cooldown gates. Canon: COMBO_ULTIMATES_PARTY_COOP_ONLY_SEP2026.md */}
        {isViewerTurn && (
          <PartyComboBar
            encounter={encounter}
            viewerUserId={viewerUserId}
            onFire={async (comboId) => {
              try {
                setSubmitting(true);
                const r = await axios.post(`${API}/encounter/${encounter.encounter_id}/combo-ult`, { combo_id: comboId });
                if (r.data?.encounter) setEncounter(r.data.encounter);
                toast.success("🤝 Combo Ultimate unleashed!");
              } catch (e) {
                const msg = e.response?.data?.detail || "Combo failed to fire.";
                setError(msg);
                toast.error(msg);
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={submitting}
          />
        )}

        {/* Action bar (only when it's the viewer's turn) — 2026-03-01: legacy
            Heavy/Light + ad-hoc skill row swapped for reusable SkillPicker
            reading the /encounter/skills kit. */}
        {isViewerTurn && (
          <div className="mb-3 flex items-start gap-2" data-testid="party-combat-action-bar">
            <div className="flex-1 min-w-0">
              <SkillPicker
                fightId={encounter?.encounter_id}
                mana={encounter?.viewer_mana ?? encounter?.mana ?? 0}
                manaCap={encounter?.viewer_mana_cap ?? encounter?.mana_cap ?? 100}
                skillCooldowns={encounter?.viewer_skill_cooldowns || {}}
                currentTurn={encounter?.turn ?? 0}
                kitEndpoint="/encounter/skills"
                onPick={onMove}
                autoPickSecs={25}
                disabled={submitting}
              />
            </div>
            {/* 🎒 Battle Bag — Mar 5, 2026 */}
            <BattleBagButton
              disabled={submitting}
              onUse={async (cid) => {
                try {
                  setSubmitting(true);
                  const r = await axios.post(`${API}/encounter/${encounter.encounter_id}/use-item`, { consumable_id: cid });
                  if (r.data?.encounter) setEncounter(r.data.encounter);
                  toast.success("🎒 Item used!");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Item use failed.");
                  throw e;
                } finally {
                  setSubmitting(false);
                }
              }}
            />
          </div>
        )}

        {/* Drops panel (on victory) */}
        {encounter.status === "won" && encounter.drops && (
          <DropsPanel drops={encounter.drops} viewerUserId={viewerUserId} />
        )}

        {/* Battle log */}
        <div className="rounded-lg bg-black/70 border border-amber-900/40 p-3 max-h-64 overflow-y-auto text-[11px] text-neutral-300 leading-relaxed font-mono mb-3"
             data-testid="party-combat-log">
          {(encounter.log || []).slice(-25).map((e, i) => (
            <div key={i}
                 className={`py-0.5 border-b border-neutral-900/50 last:border-0 ${
                   e.crit ? "text-amber-200 font-bold" :
                   e.type === "mob_attack" ? "text-red-300" :
                   e.type === "hero_attack" ? "text-emerald-200" :
                   e.type === "round_start" ? "text-purple-300 font-bold" :
                   "text-neutral-400"
                 }`}>
              {e.msg || e.type}
            </div>
          ))}
        </div>

        {error && (
          <div className="rounded-md bg-red-950/60 border border-red-700/60 text-red-200 text-xs p-2 mb-3"
               data-testid="party-combat-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}


function StatusBadges({ effects, testid }) {
  if (!effects || effects.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={testid || "status-badges"}>
      {effects.map((e, i) => (
        <span
          key={`${e.type}-${i}`}
          title={`${e.label}${e.value ? ` · ${Math.round(e.value)}` : ""} · ${e.duration}t left`}
          data-testid={`fx-${e.type}`}
          className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
            e.good
              ? "bg-emerald-900/40 border-emerald-600/40 text-emerald-200"
              : "bg-red-900/40 border-red-600/40 text-red-200"
          }`}
        >
          {e.icon} {e.label}
          {e.duration > 0 && <span className="opacity-70 ml-0.5">{e.duration}t</span>}
        </span>
      ))}
    </div>
  );
}


function MobBattleCard({ mob, isCurrent, status, role, effect }) {
  const hpPct = mob && mob.max_hp ? Math.max(0, Math.min(100, (mob.current_hp / mob.max_hp) * 100)) : 0;
  return (
    <div
      className={`relative rounded-2xl border ${
        isCurrent ? "border-red-500 shadow-[0_0_24px_rgba(220,38,38,0.5)]" : "border-red-900/40"
      } bg-gradient-to-br from-[#2a0010] via-black to-[#1a0500] p-4`}
      data-testid="party-combat-mob-card"
    >
      <div className="flex items-stretch gap-4">
        <div className="w-32 sm:w-40 shrink-0">
          <SpriteFighter actor={mob} side="enemy" isEnemy role={role} effect={effect} variant="full" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xl font-black text-red-200 truncate">{mob.name}</div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-900/60 text-red-200 font-black tracking-wider">
              Lv {mob.lv}
            </span>
            {mob.is_map_boss && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-700/60 text-amber-200 font-black tracking-wider">
                MAP BOSS
              </span>
            )}
          </div>
          <div className="text-[10px] text-neutral-400 italic mt-0.5">{mob.tagline}</div>
          <StatusBadges effects={mob.status_effects} testid="mob-status-badges" />
          <div className="mt-2">
            <div className="flex items-center justify-between text-[10px] mb-0.5">
              <span className="text-neutral-400">HP</span>
              <span className="font-mono text-red-200">{mob.current_hp} / {mob.max_hp}</span>
            </div>
            <div className="w-full h-3 rounded-full bg-black/60 border border-red-900/40 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-700 via-red-500 to-red-300 transition-all duration-500"
                style={{ width: `${hpPct}%` }}
                data-testid="party-combat-mob-hp-bar"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function HeroBattleCard({ hero, isCurrent, role, effect }) {
  // 🩹 Feb 2026 owner fix (#27): clamp displayed current HP so post-heal
  // and post-victory panels never render "HP 748 / 734".
  const displayCurHp = Math.max(0, Math.min(hero.current_hp ?? 0, hero.max_hp ?? 0));
  const hpPct = hero.max_hp ? Math.max(0, Math.min(100, (displayCurHp / hero.max_hp) * 100)) : 0;
  const dead = hero.current_hp <= 0 || !hero.alive;
  // 🎨 Identity color outline (Mar 5, 2026). Sloane blooms pink/green, Roc
  // amber, Bram slate, etc. Falls back to the default indigo styling when
  // the hero has no bound identity color. Suppressed on dead/current so
  // those states retain their own visual language.
  const idc = hero.identity_color || null;
  const idBorderStyle = (idc && !dead && !isCurrent)
    ? {
        borderColor: idc.hex,
        boxShadow: idc.hex_secondary
          ? `0 0 12px ${idc.glow}, inset 0 0 8px ${idc.glow_secondary}`
          : `0 0 10px ${idc.glow}`,
      }
    : undefined;
  return (
    <div
      className={`rounded-xl p-3 border transition-all ${
        dead ? "bg-black/60 border-zinc-800 grayscale opacity-60" :
        isCurrent ? "bg-indigo-950/70 border-indigo-400 shadow-[0_0_20px_rgba(99,102,241,0.5)]" :
        hero.is_viewer ? "bg-indigo-950/60 border-indigo-700/50" :
        "bg-black/50 border-indigo-900/40"
      }`}
      style={idBorderStyle}
      data-testid={`party-combat-hero-${hero.user_id}`}
    >
      <div className="flex items-stretch gap-2">
        <div className="w-14 shrink-0">
          <SpriteFighter actor={hero} side="player" role={role} effect={effect} isEnemy={false} variant="compact" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          {/* 🎭 Mar 4, 2026 owner drop — Hero nameplate (character) on
              top, player's username underneath. Falls back to just the
              username when the class has no authored nameplate yet. */}
          {hero.hero_nameplate ? (
            <>
              <div
                className="text-sm font-black text-amber-100 truncate leading-tight"
                data-testid={`hero-nameplate-${hero.user_id}`}
                title={hero.hero_nameplate}
              >
                {hero.hero_nameplate}
                {dead && <span className="ml-1 text-red-400 text-[10px]">💀 DOWN</span>}
              </div>
              <div
                className="text-[10px] text-neutral-400 truncate leading-tight"
                data-testid={`hero-username-${hero.user_id}`}
              >
                {hero.is_viewer ? "👑 " : ""}@{hero.name}
              </div>
            </>
          ) : (
            <div className="text-sm font-black text-indigo-100 truncate">
              {hero.is_viewer ? "👑 You" : hero.name}
              {dead && <span className="ml-1 text-red-400 text-[10px]">💀 DOWN</span>}
            </div>
          )}
          <div className="text-[9px] text-neutral-500">
            {hero.hero_class_label || hero.hero_class || "\u00A0"}
          </div>
          <StatusBadges effects={hero.status_effects} testid={`hero-status-${hero.user_id}`} />
        </div>
      </div>
      <div className="mt-2">
        <div className="flex items-center justify-between text-[10px] mb-0.5">
          <span className="text-neutral-400">HP</span>
          <span className="font-mono text-emerald-300">{displayCurHp} / {hero.max_hp}</span>
        </div>
        <div className="w-full h-2 rounded-full bg-black/60 border border-emerald-900/40 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              hpPct > 50 ? "bg-gradient-to-r from-emerald-700 to-emerald-400" :
              hpPct > 20 ? "bg-gradient-to-r from-amber-700 to-amber-400" :
              "bg-gradient-to-r from-red-800 to-red-500"
            }`}
            style={{ width: `${hpPct}%` }}
          />
        </div>
      </div>
      {/* 🎯 AFK Kit Preview (Mar 4, 2026) — surfaces the exact skill the
          server will auto-fire if this hero times out. Only shown on the
          hero whose turn it is, so the tag stays out of the way otherwise. */}
      {isCurrent && !dead && hero.afk_generator && (
        <div
          className="mt-2 flex items-center gap-1.5 text-[9px] font-semibold text-amber-200/90 bg-black/40 border border-amber-900/40 rounded px-1.5 py-0.5"
          data-testid={`hero-afk-preview-${hero.user_id}`}
          title={`If ${hero.is_viewer ? "you" : hero.name} time out, the server will auto-fire ${hero.afk_generator.name}.`}
        >
          <span>⏳ AFK →</span>
          <span className="text-amber-100">
            {hero.afk_generator.icon} {hero.afk_generator.name}
          </span>
        </div>
      )}
    </div>
  );
}


function DropsPanel({ drops, viewerUserId }) {
  if (!drops) return null;
  const me = (drops.per_hero || []).find((d) => d.user_id === viewerUserId);
  return (
    <div className="rounded-2xl border border-amber-600/50 bg-gradient-to-br from-amber-950/40 via-black to-amber-950/40 p-4 mb-3"
         data-testid="party-combat-drops">
      <div className="text-center mb-3">
        <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400">Spoils of War</div>
        <div className="text-xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
          🎁 LOOT DISTRIBUTED
        </div>
        <div className="text-[11px] text-neutral-400 mt-1">
          Party of {drops.party_size}
          {drops.tier_bonus > 0 && <span className="text-purple-300 ml-2 italic">· Cari's +{drops.tier_bonus} tier upgrade</span>}
        </div>
      </div>

      {me && (
        <div className="rounded-lg bg-black/60 border border-amber-700/60 p-3 mb-3"
             data-testid="party-combat-my-drops">
          <div className="text-[10px] uppercase tracking-wider text-amber-400 mb-2">Your Share</div>
          <div className="text-sm space-y-1">
            <div>🪙 <span className="font-black text-amber-200">{me.chits}</span> Chits
              {me.to_grainhouse > 0 && <span className="text-[10px] text-neutral-500"> · {me.to_grainhouse} → 🌾 Safe</span>}
            </div>
            {me.pet_xp > 0 && <div>🐦 <span className="font-black text-purple-200">+{me.pet_xp}</span> Pet XP</div>}
            {me.material && (
              <div>{me.material.icon} <span className="font-bold text-cyan-200">{me.material.name}</span> <span className="text-[10px] text-neutral-500">×1</span></div>
            )}
            {me.gear && (
              <div>🎁 <span className="font-bold text-pink-200">{me.gear.name}</span> <span className="text-[10px] text-neutral-500">· {me.gear.rarity}</span></div>
            )}
          </div>
        </div>
      )}

      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Party Drops</div>
      <div className="space-y-1 text-[11px]">
        {(drops.per_hero || []).map((d) => (
          <div key={d.user_id} className="flex items-center justify-between gap-2 rounded bg-black/40 px-2 py-1"
               data-testid={`party-combat-hero-drops-${d.user_id}`}>
            <span className="text-amber-200 font-bold truncate">{d.user_id === viewerUserId ? "You" : d.name}</span>
            <span className="text-neutral-300 text-right">
              🪙 {d.chits}
              {d.pet_xp ? ` · 🐦 +${d.pet_xp}` : ""}
              {d.material ? ` · ${d.material.icon}` : ""}
              {d.gear ? ` · 🎁 ${d.gear.rarity}` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═════════════════ 🤝 Party Combo Ult Bar ═════════════════
// Renders one button per eligible Combo Ultimate. A combo is eligible
// when BOTH partner classes are present in the encounter, BOTH alive,
// and the shared encounter CD is 0. Firing goes through
// POST /api/encounter/{id}/combo-ult (see party_combat_service.execute_combo_ult).
function PartyComboBar({ encounter, viewerUserId, onFire, disabled }) {
  const [combos, setCombos] = useState([]);
  const [firing, setFiring] = useState(null);

  // Fetch the catalog once — combos are static seed data.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/combo-ultimates`).then((r) => {
      // Endpoint returns { items: [...] } (previously `.combos`).
      const list = r.data?.items || r.data?.combos || [];
      if (!cancelled) setCombos(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!encounter || !combos.length) return null;

  const heroes = encounter.heroes || [];
  const combosCd = encounter.combos_cd || {};
  const classesInParty = new Set(heroes.map((h) => String(h.class_id || "")));

  // Filter: both partners must be in the party
  const eligible = combos.filter((c) => {
    const main = String(c.main_class_id || "");
    const partner = String(c.partner_class_id || "");
    return main && partner && classesInParty.has(main) && classesInParty.has(partner);
  });
  if (!eligible.length) return null;

  // 🩸 HP-sacrifice detector — reads combo's `mechanic` bullets exactly like
  // the backend's `execute_combo_ult` does. If the combo taxes the partner's
  // HP, the partner must have HP > tax-threshold or the bar disables.
  const hpTaxPct = (c) => {
    for (const line of (c.mechanic || [])) {
      const low = String(line).toLowerCase();
      if (low.includes("sacrifices") && low.includes("15%")) return 0.15;
      if (low.includes("10%") && (low.includes("sacrifice") || low.includes("hp"))) return 0.10;
    }
    return 0;
  };

  return (
    <div className="mb-3 rounded-lg border border-amber-700/50 bg-black/50 p-2" data-testid="party-combo-bar">
      <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80 mb-1.5 px-1">🤝 Party Combos</div>
      <div className="flex flex-wrap gap-2">
        {eligible.map((c) => {
          const main = heroes.find((h) => String(h.class_id) === String(c.main_class_id));
          const partner = heroes.find((h) => String(h.class_id) === String(c.partner_class_id));
          const cost = Number(c.mana_cost || 0);
          const cdLeft = Number(combosCd[c.combo_id] || 0);
          // Payload uses `current_hp` (not `hp`) — see _public_view.
          const mainHp = Number(main?.current_hp ?? main?.hp ?? 0);
          const partnerHp = Number(partner?.current_hp ?? partner?.hp ?? 0);
          const bothAlive = mainHp > 0 && partnerHp > 0;

          // 🎯 Owner canon (Feb 2026 · APEX_WHEY combo file): "Trigger hero
          // has 80+ Mana (either partner may trigger)." Only the VIEWER
          // (the trigger) needs the mana — not both partners. Bar hides
          // the combo entirely if the viewer isn't one of the two partners.
          const viewerIsMain = main?.user_id === viewerUserId;
          const viewerIsPartner = partner?.user_id === viewerUserId;
          if (!viewerIsMain && !viewerIsPartner) return null;
          const trigger = viewerIsMain ? main : partner;
          const triggerMana = int(trigger?.mana);
          const manaOk = cost === 0 || triggerMana >= cost;

          // 🩸 HP-sacrifice gate — for combos like Bram × David "Fuel to
          // the Fire", the partner sacrifices 15% HP. Backend caps at
          // "cannot self-KO — leaves David with 1 HP", so we just require
          // partner HP > 1 to be safe.
          const tax = hpTaxPct(c);
          const hpOk = tax === 0 || partnerHp > 1;

          const ready = cdLeft === 0 && bothAlive && manaOk && hpOk && !disabled && !firing;
          const reason =
            cdLeft > 0                ? `CD ${cdLeft}t`
            : !bothAlive              ? "partner down"
            : !manaOk                 ? `${triggerMana}/${cost}💠`
            : !hpOk                   ? "no HP to sac"
            : "";

          return (
            <button
              key={c.combo_id}
              onClick={async () => {
                if (!ready) return;
                setFiring(c.combo_id);
                await onFire(c.combo_id);
                setFiring(null);
              }}
              disabled={!ready}
              data-testid={`combo-fire-${c.combo_id}`}
              className={`text-left px-2.5 py-1.5 rounded border text-[11px] transition ${
                ready
                  ? "border-amber-400 bg-amber-900/40 hover:bg-amber-900/60 text-white"
                  : "border-neutral-700 bg-neutral-900/60 text-neutral-500 cursor-not-allowed opacity-60"
              }`}
              title={reason ? `Locked: ${reason}` : c.name}
            >
              <div className="font-bold flex items-center gap-1.5">
                <span className={ready ? "text-amber-300" : ""}>{c.icon || "🤝"}</span>
                <span className="truncate">{c.name}</span>
              </div>
              <div className="text-[9px] text-neutral-400 tabular-nums">
                {cost > 0 ? `${cost}💠 ` : ""}
                {tax > 0 ? `· ${Math.round(tax * 100)}% HP · ` : ""}
                {ready ? "READY" : reason}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Small local helper — keeps the readiness logic terse.
function int(v) { return Number(v || 0) | 0; }
